/**
 * Explains where synced health data comes from. A browser can't read Apple
 * Health or Android Health Connect (no web API) — only the native companion
 * app can, and it pushes snapshots to the API. Used wherever the web app shows
 * health-synced data (Health Data, Progress → Steps, Nutrition).
 *
 * When the companion app is live in the App Store / Play Store, flip
 * COMPANION_APP_AVAILABLE to true: the copy switches from "coming soon" to
 * "install the app".
 */

import { Link } from 'react-router';
import { Smartphone } from 'lucide-react';
import { useBrand } from '@/lib/useBrand';

export const COMPANION_APP_AVAILABLE = false;

/** Full explanation card (empty states). */
export function HealthSyncNotice({ showMeasurementsLink = true }: { showMeasurementsLink?: boolean }) {
  const brand = useBrand();
  return (
    <div className="card text-left">
      <div className="flex items-center gap-2 mb-2">
        <Smartphone size={16} style={{ color: 'var(--champagne)' }} />
        <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>Health data syncs from your phone</p>
      </div>
      <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>
        Steps, heart rate, sleep and weight from <strong style={{ color: 'var(--cream-dim)' }}>Apple Health</strong> or{' '}
        <strong style={{ color: 'var(--cream-dim)' }}>Android Health Connect</strong> can only be shared through the{' '}
        {brand.name} app installed on your phone. A web browser can't access that data, so it won't sync from this website on its own.
      </p>
      <p className="text-sm mt-2" style={{ color: 'var(--cream-muted)' }}>
        {COMPANION_APP_AVAILABLE ? (
          <>Install the {brand.name} app on your iPhone or Android phone, sign in, and connect your health data. It will appear here automatically.</>
        ) : (
          <>
            <strong style={{ color: 'var(--champagne)' }}>The {brand.name} app for iPhone and Android is coming soon.</strong>{' '}
            We'll let you know as soon as it's ready to download.
          </>
        )}
      </p>
      <p className="text-sm mt-2" style={{ color: 'var(--cream-muted)' }}>
        More integrations are coming soon, so you'll have more ways to bring your health and fitness data into {brand.name}.
      </p>
      {showMeasurementsLink && (
        <p className="text-xs mt-3" style={{ color: 'var(--gray-500)' }}>
          In the meantime you can log your weight and body measurements in{' '}
          <Link to="/profile/measurements" style={{ color: 'var(--gold)' }}>Body Measurements</Link>.
        </p>
      )}
    </div>
  );
}
